import { cn, PublicProfileContent } from '@up2stay/up2promo-ui';
import type { PublicProfileStyling } from '@up2stay/up2promo-ui';
import { useEffect, useMemo } from 'react';

import { PhonePreviewFrame } from '@/core/components/layout';
import { getAbsoluteFileUrl, getApiBaseUrl } from '@/core/lib/file-url';
import { stylingToFormSlice } from '@/core/lib/profile-styling-form';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { usePreferencesStore } from '@/core/store/preferences.store';
import { applyThemeForSetting, getSystemTheme } from '@/core/utils/theme';

import { env } from '@/core/env';

const profileBaseUrl =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { VITE_PROFILE_VIEWER_DOMAIN?: string } }).env
      ?.VITE_PROFILE_VIEWER_DOMAIN) ||
  '';

/** API origin for public VCF / file URLs (same contract as profile-viewer). */
function resolveApiOrigin(): string {
  try {
    return new URL(getApiBaseUrl()).origin;
  } catch {
    return getApiBaseUrl().replace(/\/$/, '');
  }
}

interface IPhonePreviewProps {
  username?: string | null;
  workspaceId?: string | null;
  /** Workspace username (slug) for team member profile links. */
  workspaceUsername?: string | null;
  profileData?: {
    id?: string;
    title?: string | null;
    jobTitle?: string | null;
    company?: string | null;
    location?: string | null;
    profileImage?: { path: string } | null;
    showProfileImage?: boolean;
    showUp2promoBranding?: boolean;
    styling?: PublicProfileStyling | null;
    ownerPhoto?: { path: string } | null;
    workspacePhoto?: { path: string } | null;
    modules?: unknown[];
    leadForm?: {
      enabled: boolean;
      visibilityMode?: 'off' | 'manual' | 'auto_open';
      autoOpenDelay?: number;
      fields?: Array<{
        id?: string;
        type: string;
        label: string;
        required: boolean;
        order: number;
        config?: { key?: string; options?: string[] };
      }>;
    };
  } | null;
  className?: string;
  /** Override file URL resolution (e.g. template preview sample avatar from `public/`). */
  getFileUrl?: (path: string) => string;
}

export function IPhonePreview({
  username,
  workspaceId,
  workspaceUsername,
  profileData,
  className,
  getFileUrl: getFileUrlProp,
}: IPhonePreviewProps) {
  const dict = useDictionary();
  const resolveFileUrl = getFileUrlProp ?? getAbsoluteFileUrl;
  const themeSetting = usePreferencesStore((state) => state.themeSetting);

  const currentTheme = useMemo(() => {
    if (themeSetting === 'system') {
      return getSystemTheme();
    }
    return themeSetting;
  }, [themeSetting]);

  useEffect(() => {
    applyThemeForSetting(currentTheme);
  }, [currentTheme]);

  const previewProfile = profileData
    ? {
        ...profileData,
        showUp2promoBranding: profileData.showUp2promoBranding ?? true,
        styling: stylingToFormSlice(
          profileData.styling,
        ) as PublicProfileStyling,
      }
    : null;

  const previewContent = (
    <div
      className={cn(
        'profile-phone-preview h-full w-full overflow-hidden app-gradient-bg text-foreground',
        currentTheme === 'dark' && 'dark',
      )}
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      <PublicProfileContent
        profile={previewProfile}
        isLoading={false}
        error={null}
        username={username || ''}
        workspaceId={workspaceId}
        workspaceUsername={workspaceUsername}
        isPreview={true}
        dictionary={dict}
        googleMapsApiKey={env.VITE_GOOGLE_MAPS_API_KEY}
        profileBaseUrl={profileBaseUrl}
        fileBaseUrl={resolveApiOrigin()}
        getFileUrl={resolveFileUrl}
      />
    </div>
  );

  return (
    <PhonePreviewFrame className={className}>
      {previewContent}
    </PhonePreviewFrame>
  );
}
